import React from 'react';
import { Inbox } from 'lucide-react';

/**
 * EmptyState — single source of truth for empty-data UIs.
 *
 * Usage:
 *   <EmptyState
 *     icon={Users}                  // optional lucide icon (defaults to Inbox)
 *     title="No clients yet"
 *     description="Start by adding your first client."
 *     // Either pass an `action` JSX node...
 *     action={<button>Add Client</button>}
 *     // ...OR the shortcut combo:
 *     actionLabel="Add Client"
 *     onAction={handleAdd}
 *     tone="default"                 // 'default' | 'success' | 'warning'
 *     size="default"                 // 'small' | 'default' | 'large'
 *   />
 *
 * Replaces ad-hoc "No data" text scattered across the app.
 */
const SIZES = {
  small:   { container: 'py-6',  iconBox: 'w-12 h-12', icon: 'w-6 h-6',  title: 'text-sm' },
  default: { container: 'py-10', iconBox: 'w-14 h-14', icon: 'w-7 h-7',  title: 'text-sm' },
  large:   { container: 'py-16', iconBox: 'w-20 h-20', icon: 'w-10 h-10', title: 'text-base' },
};

const TONES = {
  default: { bg: 'bg-neutral-100', iconColor: 'text-neutral-400', titleColor: 'text-neutral-700' },
  success: { bg: 'bg-emerald-50',  iconColor: 'text-emerald-500', titleColor: 'text-emerald-800' },
  warning: { bg: 'bg-amber-50',    iconColor: 'text-amber-500',   titleColor: 'text-amber-800' },
};

export default function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  actionLabel,
  onAction,
  tone = 'default',
  size = 'default',
  className = '',
  testId,
}) {
  const s = SIZES[size] || SIZES.default;
  const t = TONES[tone] || TONES.default;

  // Render either the explicit `action` JSX or the shortcut button
  const actionNode = action || (actionLabel && onAction ? (
    <button
      type="button"
      onClick={onAction}
      className="px-4 py-1.5 text-xs font-medium rounded-lg bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50 transition-colors shadow-sm"
      data-testid={testId ? `${testId}-action` : 'empty-state-action'}
    >
      {actionLabel}
    </button>
  ) : null);

  return (
    <div
      className={`text-center ${s.container} px-4 ${className}`}
      data-testid={testId || 'empty-state'}
      role="status"
      aria-live="polite"
    >
      <div className={`${s.iconBox} rounded-full ${t.bg} flex items-center justify-center mx-auto mb-3`}>
        <Icon className={`${s.icon} ${t.iconColor}`} aria-hidden="true" />
      </div>
      {title && <p className={`${s.title} font-semibold ${t.titleColor}`}>{title}</p>}
      {description && (
        <p className="text-xs text-neutral-500 mt-1 max-w-xs mx-auto">{description}</p>
      )}
      {actionNode && <div className="mt-4">{actionNode}</div>}
    </div>
  );
}
